import { z } from "zod";
import { request } from "./defs.ts";
import { projectIdSchema } from "./schemas.ts";

/** One matching line. `column`/`length` are 1-based / UTF-16 units of the ORIGINAL line (Monaco coordinates). */
export const projectSearchMatchSchema = z.strictObject({
  line: z.number().int().min(1),
  column: z.number().int().min(1),
  length: z.number().int().min(0),
  /** Short snippet of the line (trimmed / windowed around the match). */
  text: z.string().max(400),
  /** 0-based offset of the match inside `text` (for highlighting). */
  textStart: z.number().int().min(0),
});

export const projectSearchFileSchema = z.strictObject({
  /** Project-relative POSIX path (never an absolute Host path). */
  path: z.string(),
  /** True when the file name / path matched the query (the file is shown even without content matches). */
  nameMatch: z.boolean(),
  matches: z.array(projectSearchMatchSchema).max(1000),
});

export const projectSearchResponseSchema = z.strictObject({
  files: z.array(projectSearchFileSchema).max(200),
  /** True when a limit cut the results short (more is available; refine the query). */
  truncated: z.boolean(),
  engine: z.enum(["ripgrep", "fallback"]),
});

export type ProjectSearchMatch = z.infer<typeof projectSearchMatchSchema>;
export type ProjectSearchFile = z.infer<typeof projectSearchFileSchema>;
export type ProjectSearchResponse = z.infer<typeof projectSearchResponseSchema>;

/** One literal query over file names, relative paths and file contents of a single Project. */
export const projectSearchRequest = request(
  "filesystem",
  "searchProject",
  z.strictObject({
    projectId: projectIdSchema,
    query: z.string().min(1).max(200),
    maxFiles: z.number().int().min(1).max(200).default(100),
    maxMatches: z.number().int().min(1).max(1000).default(500),
  }),
  projectSearchResponseSchema,
);
